import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  useGetContractsAverageLengthQuery,
  useGetContractsDetailsQuery,
  useLazyGetContractsExportDetailsQuery,
} from '@client/api';
import { Card, Pagination, Typography, useRouteDocumentTitle, exportToExcel, Skeleton } from '@client/toolkit';
import { ReportingContractLengthCard } from './components/reporting-contract-length-card';
import { ContractLengthTable } from './components/contract-length-table';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { ReportContractLengthMenu } from './components/menu';
import { FaCalendarAlt } from 'react-icons/fa';
import { ReportingPaths } from '../reporting.router';
import dayjs from 'dayjs';
import { transformJsonToExcelFormat } from './utils/transformToExcelJson';
import { SortDirection } from '../components/expanable-table-wraper';

export function ReportingContractLengthRoute() {
  const { t } = useTranslation('reporting');
  const { t: tCommon } = useTranslation('common');
  const [searchParams] = useSearchParams();
  useRouteDocumentTitle(t('reporting.documentTitle'), ReportingPaths.ContractLength);

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(undefined);
  const [exporting, setExporting] = useState(false);

  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');
  
  const getDefaultStartDate = () => {
    const now = dayjs();
    const startOfYear = now.startOf('year').toDate();
    const thirtyDaysAgo = now.subtract(30, 'day').toDate();
    return startOfYear < thirtyDaysAgo ? startOfYear : thirtyDaysAgo;
  };
  
  const initialStartDate = startDateParam ? dayjs(startDateParam).toDate() : getDefaultStartDate();
  const initialEndDate = endDateParam ? dayjs(endDateParam).toDate() : new Date();

  const [timeRange, setTimeRange] = useState<{ startDate?: Date; endDate?: Date }>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });
  const [trigger] = useLazyGetContractsExportDetailsQuery();
  const onExportExcel = useCallback(async () => {
    setExporting(true);
    const res = await trigger({
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
    });
    if (res.isSuccess && res.data) {
      exportToExcel(transformJsonToExcelFormat(res.data, selectedRows), 'report-contract-length.xlsx');
    }
    setExporting(false);
  }, [trigger, timeRange, selectedRows]);
  const { data: summaryData, isFetching: isFetchingSummary } = useGetContractsAverageLengthQuery(
    {
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
    },
    {
      skip: !timeRange.startDate || !timeRange.endDate,
    },
  );

  const { data: detailsData, isFetching: isFetchingDetails } = useGetContractsDetailsQuery(
    {
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
      page: currentPage,
      pageSize: 10,
      orderBy: sortColumn && sortDirection ? `${sortColumn}:${sortDirection}` : undefined,
    },
    {
      skip: !timeRange.startDate || !timeRange.endDate,
    },
  );

  return (
    <ReportPageContent
      breadcrumb={t('contractLengthTitle')}
      menu={
        <ReportContractLengthMenu
          startDate={timeRange.startDate ?? null}
          endDate={timeRange.endDate ?? null}
          onDateChange={(startDate, endDate) => {
            setTimeRange({ startDate, endDate });
            setCurrentPage(0);
            setSelectedRows(new Set());
          }}
          onExportExcel={onExportExcel}
          exporting={exporting}
        />
      }
    >
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <ReportingContractLengthCard
          title={t('averageContractLength')}
          value={summaryData?.averageContractLengthYears ?? 0}
          unit={tCommon('year')}
          loading={isFetchingSummary}
        />
        <ReportingContractLengthCard
          title={t('averageRemainingContractLength')}
          value={summaryData?.averageRemainingContractLengthYears ?? 0}
          unit={tCommon('year')}
          loading={isFetchingSummary}
        />
        <ReportingContractLengthCard
          title={t('developmentInMonths')}
          value={summaryData?.developmentMonths ?? 0}
          showDirection
          unit={tCommon('months')}
          loading={isFetchingSummary}
        />
      </div>
      <div>
        <span className="flex items-baseline gap-2 mb-4 text-sm">
          <FaCalendarAlt className="text-white" />
          <Typography className="text-white text-sm">{t('custom')}</Typography>
        </span>
        {detailsData && (
          <Card className="mb-4">
            <ContractLengthTable
              loading={isFetchingDetails}
              data={detailsData}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={(col, direction) => {
                setSortColumn(col);
                setSortDirection(direction);
                setCurrentPage(0);
              }}
              onSelected={(ids) => {
                setSelectedRows(ids);
              }}
            />
            <Pagination
              totalCount={detailsData.total}
              currentPage={currentPage}
              setPage={setCurrentPage}
              pageSize={detailsData.pageSize}
              visible
            />
          </Card>
        )}
      </div>
    </ReportPageContent>
  );
}
